import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { awardEmoji, awardNameKey } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import { actionSx, barSx, FREAKS_GOLD, freaksSx as s } from './freaksStyles';

const REAL_ICON = { freak: '🗺️', track: '📍', catch: '🎣', reel: '🎬' };

// Daily quests (2 in the game + 1 done for real in Freakland) and my medals & badges.
export default function FreaksQuests({ username, quests, disabled, onClaim }) {
  const { t } = useFreaksT();
  const [awards, setAwards] = useState(null);

  const loadAwards = useCallback(async () => {
    try {
      setAwards((await agent.TheFreaks.profile(username)).awards ?? []);
    } catch {
      setAwards([]);
    }
  }, [username]);

  useEffect(() => {
    loadAwards();
  }, [loadAwards]);

  return (
    <Box data-testid="freaks-quests">
      <Typography sx={s.intro}>{t('theFreaks.quests.intro')}</Typography>
      {quests.map((quest) => (
        <Box key={quest.key} sx={{ ...s.card, opacity: quest.claimed ? 0.6 : 1 }}>
          <Box sx={s.between}>
            <Box sx={s.grow}>
              <Typography sx={s.title}>
                {quest.real ? `${REAL_ICON[quest.real] ?? '⭐'} ` : ''}{t(`theFreaks.quests.names.${quest.key}`, { goal: quest.goal })}
              </Typography>
              {quest.real ? <Typography sx={{ ...s.small, ...s.muted }}>{t('theFreaks.quests.realHint')}</Typography> : null}
              <Typography sx={s.small}>
                {t('theFreaks.quests.reward', { cash: quest.cash, respect: quest.respect })}
                {quest.points ? ` · ${t('theFreaks.quests.points', { points: quest.points })}` : ''}
              </Typography>
            </Box>
            {quest.claimed ? (
              <Typography sx={{ ...s.bold, color: DEFAULT_THEME_COLOR, flexShrink: 0 }}>✓ {t('theFreaks.quests.claimed')}</Typography>
            ) : (
              <Button
                data-testid={`freaks-quest-${quest.key}`}
                disabled={disabled || !quest.done}
                onClick={() => onClaim(quest)}
                sx={actionSx()}
              >
                {t('theFreaks.quests.claim')}
              </Button>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={quest.goal ? Math.min(100, (quest.progress / quest.goal) * 100) : 0}
            sx={{ ...barSx(quest.done ? FREAKS_GOLD : DEFAULT_THEME_COLOR), mt: 1.25 }}
          />
          <Typography sx={{ ...s.small, ...s.muted, mt: 0.5 }}>{quest.progress}/{quest.goal}</Typography>
        </Box>
      ))}

      <Typography sx={s.sectionTitle}>{t('theFreaks.awards.title')}</Typography>
      <Typography sx={{ ...s.small, ...s.muted, mb: 1 }}>{t('theFreaks.awards.hint')}</Typography>
      {awards === null ? null : !awards.length ? (
        <Typography sx={s.empty} data-testid="freaks-awards-empty">{t('theFreaks.awards.empty')}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} data-testid="freaks-awards">
          {awards.map((award) => (
            <Box
              key={`${award.kind}-${award.key}-${award.season}`}
              sx={{ px: 1.25, py: 0.75, borderRadius: 4, backgroundColor: 'rgba(245,197,66,0.13)', color: 'white', fontSize: 12 }}
            >
              {awardEmoji(award.key)} {t(awardNameKey(award.key))}{award.season ? ` · ${award.season}` : ''}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
